import { create } from "zustand";
import { tvApi } from "../api/tvApi.jsx";

const useTVStore = create((set, get) => ({
  // 현재 현황판 정보
  currentTV: null,
  tvs: [],

  // 컨텐츠 관련
  contents: [],
  currentContentIndex: 0,
  isPlaying: true,

  // 음악 관련
  music: [],
  currentMusicIndex: 0,
  isMusicPlaying: false,
  musicVolume: 50,

  // TV 인증 관련
  tvAuthRequired: false,
  tvAuthSession: null, // { publicKey, keyId, expirationTime }
  isTvAuthenticated: false,
  tvSessionId: null,

  // PIN 인증 관련
  pinSettings: {
    enabled: false,
    code: "",
  },
  isAuthenticated: false,

  // 설정 관련
  settings: {
    transitionDuration: 1,
    transitionStyle: "fade",
  },

  // 로딩 상태
  isLoading: false,
  error: null,

  // TV 인증 관련 Actions
  checkTvAuthentication: async (tvId) => {
    try {
      set({ isLoading: true, error: null });

      const response = await tvApi.getTvAuthentication(tvId);
      if (response?.success) {
        set({
          tvAuthRequired: response.data,
          isLoading: false,
        });
        return response.data;
      }

      throw new Error("TV 인증 여부 확인 실패");
    } catch (error) {
      // console.error('TV 인증 여부 확인 실패:', error);
      set({
        error: error.message || "TV 인증 여부 확인에 실패했습니다",
        isLoading: false,
      });
      throw error;
    }
  },

  startTvAuthSession: async (tvId) => {
    try {
      set({ isLoading: true, error: null });

      const response = await tvApi.startTvAuth(tvId);
      if (response?.success && response?.data) {
        set({
          tvAuthSession: response.data,
          isLoading: false,
        });
        return response.data;
      }

      throw new Error("TV 인증 세션 시작 실패");
    } catch (error) {
      // console.error('TV 인증 세션 시작 실패:', error);
      set({
        error: error.message || "TV 인증 세션 시작에 실패했습니다",
        isLoading: false,
      });
      throw error;
    }
  },

  authenticateTv: async (tvId, authCode) => {
    try {
      set({ isLoading: true, error: null });

      const { tvAuthSession } = get();

      // 인증 세션이 없으면 먼저 시작
      let session = tvAuthSession;
      if (!session) {
        session = await get().startTvAuthSession(tvId);
      }

      const response = await tvApi.authenticateTv(
        tvId,
        authCode,
        session.keyId
      );

      if (response?.success && response?.data) {
        set({
          isTvAuthenticated: true,
          tvSessionId: response.data,
          isLoading: false,
        });
        return response.data;
      }

      throw new Error("TV 인증 실패");
    } catch (error) {
      // console.error('TV 인증 실패:', error);
      let errorMessage = "TV 인증에 실패했습니다";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  clearTvAuth: () => {
    set({
      tvAuthRequired: false,
      tvAuthSession: null,
      isTvAuthenticated: false,
      tvSessionId: null,
    });
  },

  // 기존 Actions
  setCurrentTV: (tv) => set({ currentTV: tv }),

  setTVs: (tvs) => set({ tvs }),

  addTV: (tv) =>
    set((state) => ({
      tvs: [...state.tvs, tv],
    })),

  removeTV: (tvId) =>
    set((state) => ({
      tvs: state.tvs.filter((tv) => tv.id !== tvId),
    })),

  setContents: (contents) => set({ contents }),

  addContent: (content) =>
    set((state) => ({
      contents: [...state.contents, content],
    })),

  updateContent: (index, content) =>
    set((state) => ({
      contents: state.contents.map((c, i) => (i === index ? content : c)),
    })),

  removeContent: (index) =>
    set((state) => ({
      contents: state.contents.filter((_, i) => i !== index),
    })),

  moveContent: (fromIndex, toIndex) =>
    set((state) => {
      const newContents = [...state.contents];
      const [movedContent] = newContents.splice(fromIndex, 1);
      newContents.splice(toIndex, 0, movedContent);
      return { contents: newContents };
    }),

  setCurrentContentIndex: (index) => set({ currentContentIndex: index }),

  nextContent: () =>
    set((state) => ({
      currentContentIndex:
        (state.currentContentIndex + 1) % state.contents.length,
    })),

  prevContent: () =>
    set((state) => ({
      currentContentIndex:
        state.currentContentIndex > 0
          ? state.currentContentIndex - 1
          : state.contents.length - 1,
    })),

  setIsPlaying: (isPlaying) => set({ isPlaying }),

  togglePlaying: () => set((state) => ({ isPlaying: !state.isPlaying })),

  // 음악 관련
  setMusic: (music) => set({ music }),

  addMusic: (musicItem) =>
    set((state) => ({
      music: [...state.music, musicItem],
    })),

  removeMusic: (index) =>
    set((state) => ({
      music: state.music.filter((_, i) => i !== index),
    })),

  setMusicVolume: (volume) => set({ musicVolume: volume }),

  setIsMusicPlaying: (isPlaying) => set({ isMusicPlaying: isPlaying }),

  // PIN 인증 관련
  setPinSettings: (pinSettings) => set({ pinSettings }),

  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),

  // 설정 관련
  setSettings: (settings) => set({ settings }),

  updateSettings: (newSettings) =>
    set((state) => ({
      settings: { ...state.settings, ...newSettings },
    })),

  // 유틸리티
  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  // 초기화
  resetTV: () =>
    set({
      currentTV: null,
      contents: [],
      currentContentIndex: 0,
      music: [],
      currentMusicIndex: 0,
      isAuthenticated: false,
      // TV 인증 관련도 초기화
      tvAuthRequired: false,
      tvAuthSession: null,
      isTvAuthenticated: false,
      tvSessionId: null,
    }),
}));

export default useTVStore;
